import { useController, type FieldValues } from "react-hook-form";
import { Select, type SelectChangeEvent } from "../Select";
import { BlockInput, ErrorText, Label, RequiredLabel } from "./style";
import type { ITextSelect, TextSelectBase, TextSelectControlled, TextSelectPlain } from "./types";

const optionValue = (item: ITextSelect["options"][number]) => String("value" in item ? item.value : item.id);
const optionLabel = (item: ITextSelect["options"][number]) =>
  "label" in item ? item.label : "name" in item ? item.name : null;

function Field({
  props,
  name,
  value,
  onValue,
}: {
  props: TextSelectBase;
  /** The field's name, as the native select it replaced carried it. */
  name: string | undefined;
  value: string | undefined;
  onValue: (next: string) => void;
}) {
  const { labelDescription = "Descrição do label", id, titleOption, options, isError, labelFontSize, isRequired, disabled } =
    props;
  return (
    <BlockInput>
      <div>
        <Label htmlFor={id} style={labelFontSize ? { fontSize: labelFontSize } : undefined}>
          <span>{labelDescription}</span>
          {isRequired && <RequiredLabel>Obrigatório*</RequiredLabel>}
        </Label>
        <Select
          {...(id ? { id } : {})}
          {...(name ? { name } : {})}
          {...(value !== undefined ? { value } : {})}
          {...(disabled !== undefined ? { disabled } : {})}
          emptyLabel={titleOption ?? "Selecione"}
          invalid={!!isError}
          onValueChange={onValue}
        >
          {options.map((item) => (
            <Select.Option value={optionValue(item)} key={optionValue(item)}>
              {optionLabel(item)}
            </Select.Option>
          ))}
        </Select>
        {isError && <ErrorText>{isError?.message}</ErrorText>}
      </div>
    </BlockInput>
  );
}

function ControlledTextSelect<T extends FieldValues>(props: TextSelectControlled<T>) {
  const { field } = useController({ name: props.name, control: props.control });
  return (
    <Field
      props={props}
      name={props.name}
      value={field.value == null ? "" : String(field.value)}
      onValue={(next) => {
        field.onChange(next);
        const event: SelectChangeEvent = { target: { value: next, name: props.name } };
        void props.onChange?.(event);
      }}
    />
  );
}

function PlainTextSelect(props: TextSelectPlain) {
  return (
    <Field
      props={props}
      name={props.name}
      value={props.value === undefined ? undefined : String(props.value)}
      onValue={(next) => void props.onChange?.({ target: { value: next, name: props.name ?? props.id ?? "" } })}
    />
  );
}

/**
 * A labelled select of `{ id, name }` or `{ value, label }` options on the design system's select (spec 20261028-130000
 * AC-1). Inside a react-hook-form form it takes `control` and `name`; elsewhere `value` and `onChange`.
 */
export default function TextSelect<T extends FieldValues = FieldValues>(props: ITextSelect<T>) {
  return props.control ? <ControlledTextSelect {...(props as TextSelectControlled<T>)} /> : <PlainTextSelect {...props} />;
}
